import { Typography } from '@mui/material';
import { GroupedVirtuoso } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import type { RepairPartViewModel } from '@api/enterprise/openapi';
import { PartGroupHeader } from './PartGroupHeader';
import { PartListItem } from './PartListItem';

type FilteredPartsData = {
  groups: Record<string, RepairPartViewModel[]>;
  flatList: RepairPartViewModel[];
  groupCounts: number[];
  companies: string[];
};

type SelectedPartData = {
  quantity: number;
  cost: number | null | undefined;
  partName: string | null | undefined;
  description: string | null | undefined;
};

type Props = {
  filteredParts: FilteredPartsData;
  expandedGroups: Set<string>;
  selectedParts: Map<string, SelectedPartData>;
  searchTerm: string;
  onToggleGroup: (company: string) => void;
  onTogglePart: (
    companyName: string | undefined,
    partNumber: string | undefined
  ) => void;
  onQuantityChange: (partKey: string, quantity: number) => void;
  isPartSelected: (
    companyName: string | undefined,
    partNumber: string | undefined
  ) => boolean;
  getPartQuantity: (
    companyName: string | undefined,
    partNumber: string | undefined
  ) => number;
};

export const VirtualizedPartsList = ({
  filteredParts,
  expandedGroups,
  searchTerm,
  onToggleGroup,
  onTogglePart,
  onQuantityChange,
  isPartSelected,
  getPartQuantity
}: Props) => {
  const { t } = useTranslation('parts');

  if (Object.keys(filteredParts.groups).length === 0) {
    return (
      <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
        {searchTerm
          ? t('addPartModal.noPartsFound')
          : t('addPartModal.noParts')}
      </Typography>
    );
  }

  return (
    <GroupedVirtuoso
      style={{ height: '100%' }}
      groupCounts={filteredParts.groupCounts}
      groupContent={index => {
        const company = filteredParts.companies[index];
        const partCount = filteredParts.groups[company]?.length || 0;
        const isExpanded = expandedGroups.has(company);

        return (
          <PartGroupHeader
            company={company}
            partCount={partCount}
            isExpanded={isExpanded}
            onToggle={() => onToggleGroup(company)}
          />
        );
      }}
      itemContent={_index => {
        const part = filteredParts.flatList[_index];

        if (!part) {
          return <div style={{ height: '1px', visibility: 'hidden' }} />;
        }

        const partKey = `${part.companyName}:${part.partNumber}`;
        return (
          <PartListItem
            part={part}
            isSelected={isPartSelected(part.companyName, part.partNumber)}
            quantity={getPartQuantity(part.companyName, part.partNumber)}
            onToggle={() => onTogglePart(part.companyName, part.partNumber)}
            onQuantityChange={qty => onQuantityChange(partKey, qty)}
          />
        );
      }}
    />
  );
};
